import React, { FC } from 'react'
import styles from './styles.module.scss'

type TelephoneProps = {
  placeholder: string
  value: string
  onChange: (value: string) => void
  onFocus: () => void
}

const TelephoneInput: FC<TelephoneProps> = ({
  placeholder,
  value,
  onChange,
  onFocus
}) => {
  const refs: {
    [key: string]: HTMLDivElement | null
  } = {
    main: null,
    prefix: null,
    suffix: null
  }

  const setInputRef = (name: string, element: HTMLDivElement | null) => {
    refs[name] = element
  }

  const updateElementClass = (
    element: HTMLDivElement | null,
    className: string,
    isRemove = false
  ) => {
    if (!element) {
      return
    }

    element.classList[isRemove ? 'remove' : 'add'](className)
  }

  const handleFocus = () => {
    onFocus()
    updateElementClass(refs.main, 'active')
  }

  const handleBlur = () => {
    updateElementClass(refs.main, 'active', true)
  }

  const getSubPhone = (name: string, val: string) => {
    if (!name || !val) return ''
    switch (name) {
      case 'countryCode':
        return val?.toString()?.slice(0, 2) || ''
      case 'areaCode':
        return val?.toString()?.slice(2, 5) || ''
      case 'prefix':
        return val?.toString()?.slice(5, 8) || ''
      case 'suffix':
        return val?.toString()?.slice(8) || ''
      default:
        return ''
    }
  }

  const getFullCode = (
    name: string,
    val: string,
    prevVal: string,
    clearFollowingContent = false
  ) => {
    if (!name) return ''

    switch (name) {
      case 'areaCode':
        if (clearFollowingContent) {
          return `${getSubPhone('countryCode', prevVal) || '+1'}${val}`
        }

        return `${
          getSubPhone('countryCode', prevVal) || '+1'
        }${val}${getSubPhone('prefix', prevVal)}${getSubPhone(
          'suffix',
          prevVal
        )}`

      case 'prefix':
        if (clearFollowingContent) {
          return `${getSubPhone('countryCode', prevVal) || '+1'}${
            getSubPhone('areaCode', prevVal) || '000'
          }${val}`
        }

        return `${getSubPhone('countryCode', prevVal) || '+1'}${
          getSubPhone('areaCode', prevVal) || '000'
        }${val}${getSubPhone('suffix', prevVal)}`

      case 'suffix':
        return `${getSubPhone('countryCode', prevVal) || '+1'}${
          getSubPhone('areaCode', prevVal) || '000'
        }${getSubPhone('prefix', prevVal) || '000'}${val}`
      default:
        return ''
    }
  }

  const handleValueChanged = (name: string, val: string) => {
    const areaCode = getSubPhone('areaCode', val)
    const prefix = getSubPhone('prefix', val)
    const areaCodeFilled = areaCode.length === 3
    const prefixFilled = prefix.length === 3

    if (areaCodeFilled && name === 'areaCode') {
      refs.prefix?.focus()
    } else if (prefixFilled && name === 'prefix') {
      refs.suffix?.focus()
    }
  }

  const handleChange = ({
    target: { value: val = '', name }
  }: React.ChangeEvent<HTMLInputElement>): void => {
    let valueChanged = false
    let valueDeleted = false

    const previousSubValue = getSubPhone(name, value)
    const updatedSubVal = val.replace(/[^\d]/g, '')

    if (previousSubValue.length < updatedSubVal.length) valueChanged = true

    if (previousSubValue.length > updatedSubVal.length) valueDeleted = true

    const fullValue = getFullCode(name, updatedSubVal, value, valueDeleted)

    onChange(fullValue)

    if (valueChanged) handleValueChanged(name, fullValue)
  }

  return (
    <>
      <div
        className={styles.input_container}
        ref={(node) => setInputRef('main', node)}
      >
        <div className={`${styles.parenthesis} ${styles.parenthesis_left}`}>
          &#40;
        </div>
        <input
          className={`${styles.input} ${styles.area_code}`}
          type="text"
          name="areaCode"
          placeholder={getSubPhone('areaCode', placeholder)}
          value={getSubPhone('areaCode', value)}
          onChange={handleChange}
          maxLength={3}
          onFocus={handleFocus}
          onBlur={handleBlur}
        />
        <div className={`${styles.parenthesis} ${styles.parenthesis_right}`}>
          &#41;
        </div>
        <input
          ref={(node) => setInputRef('prefix', node)}
          className={`${styles.input} ${styles.prefix}`}
          type="text"
          name="prefix"
          placeholder={getSubPhone('prefix', placeholder)}
          value={getSubPhone('prefix', value)}
          onChange={handleChange}
          maxLength={3}
          onFocus={handleFocus}
          onBlur={handleBlur}
        />
        <div className={styles.dash}>-</div>
        <input
          ref={(node) => setInputRef('suffix', node)}
          className={`${styles.input} ${styles.suffix}`}
          type="text"
          name="suffix"
          placeholder={getSubPhone('suffix', placeholder)}
          value={getSubPhone('suffix', value)}
          onChange={handleChange}
          maxLength={4}
          onFocus={handleFocus}
          onBlur={handleBlur}
        />
      </div>
    </>
  )
}

export default TelephoneInput
